import React, { useState } from "react";
import { cn } from "../../../lib/utils";
import { getAvatarIdentityTone, getAvatarInitials } from '@/lib/avatarIdentity';
import { AX1SignalPulse } from '@/components/ui/SignalPulse';
import { AVATAR_STATUS_LABEL, AVATAR_STATUS_TONE, type AvatarStatus } from './avatarPresence';

export type { AvatarStatus } from './avatarPresence';

export interface AvatarProps {
  name: string;
  src?: string | null;
  size?: number;                 // px
  status?: AvatarStatus;
  typing?: boolean;
  ring?: boolean;
  className?: string;
  alt?: string;
  colorSeed?: string;
  statusLabel?: string;
  decorative?: boolean;
  showGradientBorder?: boolean;  // Modern gradient border
  interactive?: boolean;         // Hover effects
}

export const Avatar: React.FC<AvatarProps> = ({
  name,
  src,
  size = 28,
  status,
  typing = false,
  ring = true,
  className = "",
  alt,
  colorSeed,
  statusLabel,
  decorative = false,
  showGradientBorder = false,
  interactive = false,
}) => {
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const showImage = Boolean(src && failedSrc !== src);
  const initials = getAvatarInitials(name);
  const identityTone = getAvatarIdentityTone(colorSeed || name);
  const resolvedStatusLabel = status ? (statusLabel || AVATAR_STATUS_LABEL[status]) : null;

  const dim = `${size}px`;
  const ringCls = ring
    ? size < 24 ? "ring-1 ring-surface-card" : "ring-2 ring-surface-card"
    : "";
  // Modern gradient border styling
  const gradientBorderCls = showGradientBorder
    ? "p-0.5 bg-accent-primary/10"
    : "";

  // Interactive hover effects
  const interactiveCls = interactive
    ? "transition-transform duration-200 hover:scale-105 cursor-pointer"
    : "";

  return (
    <div
      className={cn(
        "relative rounded-full shrink-0 select-none",
        showGradientBorder ? gradientBorderCls : "bg-surface-chip",
        !showGradientBorder && ringCls,
        interactiveCls,
        className
      )}
      style={{ width: dim, height: dim }}
      role={decorative ? undefined : "img"}
      aria-hidden={decorative ? true : undefined}
      aria-label={decorative ? undefined : `${alt || name}${resolvedStatusLabel ? ` — ${resolvedStatusLabel}` : ''}${typing ? " — typing…" : ""}`}
      title={decorative ? undefined : `${name}${resolvedStatusLabel ? ` · ${resolvedStatusLabel}` : ''}${typing ? " · typing…" : ""}`}
    >
      <div
        className={cn(
          "relative rounded-full overflow-hidden w-full h-full",
          showGradientBorder ? "bg-surface-chip" : ""
        )}
      >
        {showImage ? (
          <img
            src={src || ""}
            alt=""
            aria-hidden="true"
            loading="lazy"
            className={cn(
              "w-full h-full object-cover",
              showGradientBorder && ringCls
            )}
            onError={() => setFailedSrc(src || null)}
          />
        ) : (
          <div
            className={cn(
              "flex h-full w-full items-center justify-center font-semibold leading-none tracking-[-0.025em]",
              identityTone.className,
            )}
            data-avatar-fallback=""
            style={{ fontSize: `${Math.max(9, Math.round(size * 0.34))}px` }}
          >
            {initials}
          </div>
        )}
      </div>

      {typeof status !== "undefined" && (
        <span
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute -bottom-0.5 -right-0.5 z-10 inline-flex items-center justify-center rounded-full bg-surface-card ring-1 ring-surface-card",
            size < 24 ? "h-2.5 w-2.5" : "h-3 w-3",
          )}
          data-presence-indicator=""
          data-presence-status={status}
        >
          <AX1SignalPulse
            tone={AVATAR_STATUS_TONE[status]}
            size={size >= 44 ? 'md' : 'sm'}
          />
          {status === 'dnd' ? (
            <span className="absolute h-px w-1.5 rounded-full bg-text-inverse" />
          ) : null}
        </span>
      )}

      {typing && (
        <span
          aria-hidden
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            boxShadow: "0 0 0 2px rgb(var(--color-primary-500-rgb, 14 165 233) / 0.24)",
            animation: "avatarPulse 1.2s ease-in-out infinite",
          }}
        />
      )}

    </div>
  );
};

export interface StackPerson {
  id: string;
  name: string;
  avatar?: string | null;
  status?: AvatarStatus;
  typing?: boolean;
  colorSeed?: string;
}

export const AvatarStack: React.FC<{ people: StackPerson[]; max?: number; size?: number }> = ({
  people,
  max = 3,
  size = 28,
}) => {
  const shown = people.slice(0, max);
  const rest = Math.max(0, people.length - shown.length);
  return (
    <div className="isolate flex -space-x-1 items-center" data-avatar-stack="">
      {shown.map((p, index) => (
        <span
          key={p.id}
          className="relative inline-flex shrink-0 rounded-full"
          data-avatar-stack-item=""
          style={{ zIndex: shown.length - index }}
        >
          <Avatar
            name={p.name}
            src={p.avatar}
            status={p.status}
            typing={p.typing}
            colorSeed={p.colorSeed || p.id}
            size={size}
          />
        </span>
      ))}
      {rest > 0 && (
        <div
          className={cn(
            "flex items-center justify-center rounded-full bg-surface-chip text-xs font-semibold text-text-secondary ring-2 ring-surface-card"
          )}
          style={{ width: `${size}px`, height: `${size}px` }}
          title={`${rest} more people`}
          aria-label={`${rest} more people`}
          data-avatar-stack-overflow=""
        >
          +{rest}
        </div>
      )}
    </div>
  );
};
